import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user && user.email) navigate("/admin", { replace: true });
    }, [user, navigate]);

    if (user && user.role && user.email) {
        return <Navigate to="/admin" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await login(email, password);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(err.message || formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-12">
                    <Logo variant="stacked" />
                </div>
                <div className="bg-brand-bgalt border border-brand-border p-10">
                    <div className="flex items-center gap-2 overline mb-6">
                        <Lock size={12} /> Espace administrateur
                    </div>
                    <h1 className="font-serif text-3xl text-brand-ink font-light">Connexion</h1>
                    <p className="mt-2 text-sm text-brand-sageDark">Accès réservé à l'équipe Japet.</p>

                    <form onSubmit={submit} className="mt-8 space-y-6" noValidate>
                        <div>
                            <label className="overline block mb-2">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="field-input"
                                data-testid="admin-login-email"
                                autoComplete="email"
                                required
                            />
                        </div>
                        <div>
                            <label className="overline block mb-2">Mot de passe</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="field-input"
                                data-testid="admin-login-password"
                                autoComplete="current-password"
                                required
                            />
                        </div>

                        {error && <p className="text-sm text-red-700" data-testid="admin-login-error">{error}</p>}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="w-full py-4 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.22em] hover:bg-brand-sage transition-colors disabled:opacity-50"
                        >
                            {submitting ? "Connexion…" : "Se connecter"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
